import { useMemo } from "react";
import { Link } from "react-router-dom";
import { usePageMeta, useJsonLd, breadcrumbJsonLd, SITE_URL } from "../lib/seo";
import { usePublicConfig } from "../hooks/usePublicConfig";

const HOWTO_JSONLD = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to buy digital goods with onchain USDC on MarketOnBase",
  description:
    "Buy a digital product on MarketOnBase with payment held in a non-custodial smart contract on Base until you have what you paid for.",
  url: `${SITE_URL}/how-it-works`,
  step: [
    {
      "@type": "HowToStep",
      name: "Sign in with your email",
      text: "Signing in with email creates a Coinbase-managed Smart Account on Base. No browser extension, seed phrase, or private key required.",
    },
    {
      "@type": "HowToStep",
      name: "Fund your wallet with USDC",
      text: "Add USDC to your Smart Account. You'll need the listed price plus the 10% platform fee.",
    },
    {
      "@type": "HowToStep",
      name: "Review the itemized total and confirm",
      text: "Checkout shows the seller's price, the platform fee, and the total, cross-checked against the smart contract's own quote before you sign.",
    },
    {
      "@type": "HowToStep",
      name: "Payment goes into the smart contract",
      text: "Your USDC moves into the smart contract on Base — not to the seller, and not to the platform.",
    },
    {
      "@type": "HowToStep",
      name: "Download and check the file",
      text: "Once funding confirms onchain, download the exact file version attached to your order and verify it is what was promised.",
    },
    {
      "@type": "HowToStep",
      name: "Release the funds, or open a dispute",
      text: "If everything is right, release the principal to the seller. If not, open a dispute before the deadline shown on your order.",
    },
  ],
};

export function HowItWorksPage() {
  const { data: publicConfig } = usePublicConfig();
  const youtubeAccount = publicConfig?.youtubeTransferGoogleAccount;

  usePageMeta({
    title: "How it works",
    description:
      "Step by step: how buying and selling digital goods with a secure smart contract on Base works, exactly when money moves, and what happens if there's a dispute.",
    path: "/how-it-works",
  });

  useJsonLd(HOWTO_JSONLD);
  useJsonLd(
    useMemo(
      () =>
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "How it works", path: "/how-it-works" },
        ]),
      [],
    ),
  );

  return (
    <article className="stack" style={{ maxWidth: 720 }}>
      <div>
        <h1>How it works</h1>
        <p className="muted">
          What happens at every step, and exactly when money moves.
        </p>
      </div>

      <section className="stack">
        <h2>For buyers</h2>
        <ol className="stack">
          <li>
            <strong>Sign in with your email.</strong> You get a Coinbase-managed Smart Account on
            Base. There&apos;s no extension to install and no seed phrase to write down. The
            account is yours — MarketOnBase never has access to your keys.
          </li>
          <li>
            <strong>Fund your wallet with USDC.</strong> You&apos;ll need the listed price plus the
            10% platform fee. Your balance is visible any time on your{" "}
            <Link to="/wallet">wallet page</Link>.
          </li>
          <li>
            <strong>Review the itemized total.</strong> Checkout shows the seller&apos;s price, the
            fee, and the total. Before you&apos;re asked to sign anything, the app cross-checks its
            own math against the smart contract&apos;s <code>quote()</code> function and refuses
            to continue if they disagree.
          </li>
          <li>
            <strong>Confirm — and your payment goes into the smart contract.</strong> The USDC moves into the
            smart contract on Base. It does not go to the seller, and it does not go to us. The
            platform fee is paid to the treasury at this moment and is nonrefundable from here on.
          </li>
          <li>
            <strong>Download and check your file.</strong> Once funding is confirmed onchain, the
            download unlocks. You always receive the exact file version that was attached to your
            order, even if the seller later replaces the file on the listing.
          </li>
          <li>
            <strong>Release, or dispute.</strong> If the product is what was promised, release the
            principal to the seller. If it isn&apos;t, open a dispute before your order&apos;s
            deadline — the countdown is shown on the order page.
          </li>
        </ol>
      </section>

      <section className="stack">
        <h2>For sellers</h2>
        <ol className="stack">
          <li>
            <strong>Create a listing.</strong> Title, description, and your price in USDC. The
            price you set is the principal you receive — the buyer pays the platform fee on top,
            so it never comes out of your side.
          </li>
          <li>
            <strong>Upload your file.</strong> It goes straight into a private quarantine bucket
            that isn&apos;t publicly reachable.
          </li>
          <li>
            <strong>Wait for the malware scan.</strong> Every upload is scanned before it can be
            sold. Clean files are promoted and become publishable; infected ones are rejected.
          </li>
          <li>
            <strong>Publish.</strong> Your listing becomes visible in{" "}
            <Link to="/">browse</Link> and gets its own indexable page.
          </li>
          <li>
            <strong>Get paid.</strong> When the buyer releases, the principal goes to your wallet.
            If the dispute window passes without the buyer releasing or disputing, you can claim
            the funds yourself — they don&apos;t sit in limbo forever.
          </li>
        </ol>
      </section>

      <section className="stack">
        <h2>Buying or selling an account (YouTube, X, TikTok)</h2>
        <p>
          An account listing works differently from a file or license key, because handing over a
          real YouTube, X, or TikTok account safely isn&apos;t something either side can do alone —
          and it isn&apos;t something any of those platforms let us automate. Instead, the account
          passes through MarketOnBase the same way your payment does: never handed directly from
          seller to buyer.
        </p>
        <ol className="stack">
          <li>
            <strong>The buyer funds the contract first,</strong> exactly like any other order — before
            any part of the account handoff begins. For a YouTube channel, the buyer also submits
            the Google account email they want added, right away, from their order page.
          </li>
          <li>
            <strong>The seller hands the account to MarketOnBase,</strong> not to the buyer. For
            YouTube, this means adding {youtubeAccount ?? "our Google account"} as an Owner on the
            channel&apos;s Brand Account — and, the same day, we add the buyer&apos;s Google
            account as an Owner too.
            Google enforces a mandatory 7-day wait before a newly-added owner can become the
            Primary Owner, which is why these orders have a longer dispute window than usual —
            adding the buyer immediately, rather than only after we&apos;ve verified everything,
            means their 7-day wait starts right away instead of days later. X and TikTok have no
            equivalent official transfer feature, so the seller resets the account&apos;s login to
            credentials only we control instead.
          </li>
          <li>
            <strong>We verify the account</strong> matches what was listed before releasing
            anything further.
          </li>
          <li>
            <strong>The buyer gets full control</strong> — for YouTube, once the 7-day wait is up,
            the buyer becomes Primary Owner directly and the seller is removed automatically; for
            others, we reset the login again and send the buyer their own credentials, which they
            reveal once from their order page.
          </li>
          <li>
            <strong>The buyer confirms full control, then releases funds</strong> — changing the
            password, email, and any linked 2FA first, so the account can&apos;t be reclaimed later.
          </li>
        </ol>
        <p className="muted text-sm">
          Every step above that happens outside MarketOnBase is done by a real person, not
          automated — no platform gives us an API that could safely do it without one.
        </p>
      </section>

      <section className="stack">
        <h2>Buying or selling a domain name</h2>
        <p>
          A domain transfer is simpler than an account transfer: there&apos;s a real, standardized
          transfer code (sometimes called an EPP or auth code) issued by the domain&apos;s
          registrar, so there&apos;s no multi-day custody hop through MarketOnBase the way a
          social account needs.
        </p>
        <ol className="stack">
          <li>
            <strong>The seller proves they control the domain</strong> before the listing can even
            publish — by adding a DNS TXT record with a token we generate. This is the only real
            way to confirm a domain listing is legitimate, since no public API tells us who owns a
            domain the way YouTube or X expose public stats.
          </li>
          <li>
            <strong>The buyer funds the order,</strong> exactly like any other order.
          </li>
          <li>
            <strong>The seller retrieves the transfer code</strong> from their registrar and
            submits it to MarketOnBase, encrypted.
          </li>
          <li>
            <strong>The buyer reveals the code</strong> from their order page and starts the
            transfer at their own registrar. Standard inter-registrar transfers take 5-7 days,
            which is why this order type gets a longer dispute window than a file or license key.
          </li>
          <li>
            <strong>The buyer confirms the transfer completed</strong> and the domain resolves as
            expected, then releases funds.
          </li>
        </ol>
        <p className="muted text-sm">
          We can&apos;t verify a transfer code actually works without attempting a real transfer —
          that happens between you and the registrars, not through us. If a code turns out to be
          invalid, that&apos;s exactly what the dispute window is for.
        </p>
      </section>

      <section className="stack">
        <h2>When a dispute is opened</h2>
        <p>
          Opening a dispute pauses release. Both sides can submit evidence: the buyer explains what
          was wrong, the seller gets to respond. An arbitrator then resolves it by splitting the
          principal between buyer and seller — it can go entirely to one side, entirely to the
          other, or be divided.
        </p>
        <p>
          The split is enforced by the contract: the buyer&apos;s share plus the seller&apos;s share
          must add up to exactly the principal. The arbitrator cannot send the money anywhere else,
          including to themselves. The 10% platform fee is not part of the disputed amount and is
          not returned.
        </p>
      </section>

      <section className="stack">
        <h2>Where the money actually is</h2>
        <p>
          Between funding and settlement, your principal sits in the smart contract on Base —
          a public address you can inspect on a block explorer. It is not in a company bank
          account, not in a pooled custodial wallet, and not on our balance sheet.
        </p>
        <p>
          In the rare case that a payout can&apos;t be delivered to an address at settlement time
          (for example, USDC&apos;s issuer has blocked it), the contract credits the amount for that
          account to claim later instead of failing the whole settlement. If that ever happens to
          you, a claim button appears on your <Link to="/wallet">wallet page</Link>.
        </p>
      </section>

      <section className="stack">
        <h2>Before you start</h2>
        <p>
          Cryptocurrency transactions are irreversible. There is no chargeback. Read the{" "}
          <Link to="/terms">terms</Link> and the <Link to="/faq">FAQ</Link>, and start with an
          amount you&apos;re comfortable with.
        </p>
      </section>
    </article>
  );
}
